import { Injectable, Optional } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { ClientService } from '../../_services/client.service';
import { ClientListing } from '../../_models/clients/client-listing.model';
import { ClientListForAddStudentResponse } from '../../_models/clients/client-list-for-add-student-response.model';
import { filterLocal } from './typeahead.component';

/**
 * Client suggestions for the client search boxes, from api/Clients/GetClientListForAddStudent. The list is fetched
 * once, the first time a box asks for it (on focus, and only while that box's switch is on), and then shared. A
 * failed fetch suggests nothing and is tried again next time.
 *
 * Every member is an arrow property so a template can pass it straight to `app-typeahead`:
 * `[source]="clients.search" [prefetch]="clients.prefetch" [displayWith]="clients.display" [valueWith]="clients.value"`.
 */
@Injectable({ providedIn: 'root' })
export class ClientSuggestionsService {

  private clients$: Observable<ClientListing[]> | null = null;

  /** ClientService is provided by AppModule, not root; without it (a spec, say) there are no client suggestions. */
  constructor(@Optional() private clientService: ClientService) { }

  /** Starts the fetch, if it has not started. */
  public readonly prefetch = (): void => {
    this.load().subscribe();
  };

  /** Clients whose "Name (Id)" contains q, those starting with it first, at most 10. */
  public readonly search = (q: string): Observable<ClientListing[]> =>
    this.load().pipe(map(clients => filterLocal(clients, q, this.display)));

  /** "Name (Id)", the convention the client pickers use. */
  public readonly display = (c: ClientListing): string => `${ClientSuggestionsService.plainName(c)} (${c?.clientId})`;

  /** What a pick puts in the box: the plain client name, which is what the client searches match on. */
  public readonly value = (c: ClientListing): string => ClientSuggestionsService.plainName(c);

  /**
   * The client's name without the " ID:123" the API appends for staff callers (ClientListForAddStudent), so the id
   * is not shown twice and a pick matches the client search.
   */
  public static plainName(c: ClientListing): string {
    return (c?.clientName ?? '').replace(/\s*ID:\s*\d+\s*$/i, '').trim();
  }

  private load(): Observable<ClientListing[]> {
    if (!this.clientService) {
      return of([] as ClientListing[]);
    }
    if (!this.clients$) {
      // A background fetch: focusing a search box does not show the spinner.
      this.clients$ = this.clientService.getClientListForAddStudent(true).pipe(
        // One entry per client: the list can hold the same client more than once.
        map((response: ClientListForAddStudentResponse) =>
          (response?.clients ?? []).filter((c, i, all) => all.findIndex(o => o.clientId === c.clientId) === i)),
        catchError(() => {
          this.clients$ = null;
          return of([] as ClientListing[]);
        }),
        shareReplay(1)
      );
    }
    return this.clients$;
  }
}
